import React from 'react';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  /* Variables and font imports are handled by global.css */

  .process-section {
    padding: 100px 60px;
    border-top: 1px solid var(--border-soft);
    background: var(--card-bg);
    position: relative;
    overflow: hidden;
  }
  .process-section::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 55% 40% at 50% 100%, rgba(245,200,0,0.04) 0%, transparent 65%);
  }

  /* ── Header ── */
  .proc-header {
    display: flex; justify-content: space-between; align-items: flex-end;
    margin-bottom: 72px; gap: 32px; flex-wrap: wrap;
    position: relative; z-index: 1;
  }
  .proc-eyebrow {
    font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 14px;
    display: flex; align-items: center; gap: 10px;
  }
  .proc-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: var(--accent); display: inline-block;
  }
  .proc-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(42px, 5.5vw, 74px);
    line-height: .95; color: var(--white);
  }
  .proc-heading em {
    font-family: 'Instrument Serif', serif;
    font-style: italic; color: var(--gold);
  }
  .proc-sub {
    max-width: 300px; font-size: 13px;
    line-height: 1.85; color: var(--muted-light);
  }

  /* ── Steps grid ── */
  .steps-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border: 1px solid var(--border-soft);
    border-radius: 6px; overflow: hidden;
    position: relative; z-index: 1;
  }

  /* ── Step card ── */
  .step-card {
    padding: 44px 32px 40px;
    border-right: 1px solid var(--border-soft);
    position: relative; overflow: hidden;
    transition: background .35s ease;
    cursor: default;
  }
  .step-card:last-child { border-right: none; }

  .step-card::before {
    content: ''; position: absolute; inset: 0;
    background: rgba(245,200,0,0.025);
    opacity: 0; transition: opacity .35s;
  }
  .step-card:hover::before { opacity: 1; }

  .step-card::after {
    content: ''; position: absolute;
    top: 0; left: 0; right: 0; height: 2px;
    background: var(--accent);
    transform: scaleX(0); transform-origin: left;
    transition: transform .4s ease;
  }
  .step-card:hover::after { transform: scaleX(1); }

  /* Ghost number */
  .step-ghost {
    position: absolute; bottom: -8px; right: 12px;
    font-family: 'Bebas Neue', sans-serif; font-size: 110px; line-height: 1;
    color: rgba(245,200,0,0.05);
    user-select: none; pointer-events: none;
    transition: color .35s, transform .35s;
  }
  .step-card:hover .step-ghost {
    color: rgba(245,200,0,0.09);
    transform: scale(1.05);
  }

  /* Phase label */
  .step-phase {
    display: inline-flex; align-items: center; gap: 7px;
    font-size: 9px; letter-spacing: 3px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 20px;
    opacity: .7; transition: opacity .3s;
  }
  .step-card:hover .step-phase { opacity: 1; }
  .step-phase-dot {
    width: 4px; height: 4px; border-radius: 50%;
    background: var(--accent); flex-shrink: 0;
  }

  /* Badge */
  .step-badge {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 50%;
    border: 1px solid rgba(245,200,0,0.2);
    background: rgba(245,200,0,0.06);
    font-family: 'Bebas Neue', sans-serif; font-size: 14px;
    color: var(--accent); margin-bottom: 22px;
    transition: background .3s, border-color .3s, color .3s;
    flex-shrink: 0;
  }
  .step-card:hover .step-badge {
    background: var(--accent); border-color: var(--accent); color: #0a0900;
  }

  /* Icon */
  .step-icon {
    width: 36px; height: 36px; color: var(--accent);
    margin-bottom: 22px; opacity: .7;
    transition: opacity .3s, transform .35s;
  }
  .step-card:hover .step-icon { opacity: 1; transform: scale(1.1); }
  .step-icon svg { width: 100%; height: 100%; }

  /* Title */
  .step-title {
    font-family: 'Bebas Neue', sans-serif; font-size: 26px;
    letter-spacing: 2px; color: var(--white);
    margin-bottom: 12px; line-height: 1;
    transition: color .3s;
  }
  .step-card:hover .step-title { color: var(--accent); }

  /* Description */
  .step-desc {
    font-size: 12px; line-height: 1.85;
    color: var(--muted-light); margin-bottom: 24px;
  }

  /* Deliverables */
  .step-deliverables {
    display: flex; flex-direction: column; gap: 6px;
    padding-top: 18px; border-top: 1px solid var(--border-soft);
  }
  .step-del-label {
    font-size: 9px; letter-spacing: 2px; text-transform: uppercase;
    color: var(--muted); margin-bottom: 4px;
  }
  .step-del-item {
    display: flex; align-items: center; gap: 7px;
    font-size: 11px; color: var(--muted-light);
  }
  .step-del-item::before {
    content: '';
    width: 5px; height: 5px; border-radius: 50%;
    background: rgba(245,200,0,0.3); flex-shrink: 0;
  }

  /* ── Bottom row ── */
  .proc-bottom {
    margin-top: 40px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 20px; flex-wrap: wrap;
    position: relative; z-index: 1;
  }
  .proc-note {
    font-size: 12px; color: var(--muted);
    max-width: 360px; line-height: 1.7;
  }
  .proc-note strong { color: var(--muted-light); font-weight: 400; }

  .proc-cta {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--accent); color: #0a0900;
    padding: 12px 30px; font-size: 11px;
    letter-spacing: 2px; text-transform: uppercase;
    border: none; border-radius: 2px;
    font-family: 'DM Sans', sans-serif; font-weight: 500;
    cursor: pointer; transition: background .25s, transform .2s;
    text-decoration: none;
  }
  .proc-cta:hover { background: var(--gold); transform: translateY(-1px); }
  .proc-cta svg { width: 13px; height: 13px; }

  /* ── Responsive ── */
  @media (max-width: 1000px) {
    .steps-grid { grid-template-columns: repeat(2, 1fr); }
    .step-card:nth-child(2) { border-right: none; }
    .step-card:nth-child(1),
    .step-card:nth-child(2) { border-bottom: 1px solid var(--border-soft); }
  }
  @media (max-width: 600px) {
    .process-section { padding: 72px 20px; }
    .steps-grid      { grid-template-columns: 1fr; }
    .step-card       { border-right: none; border-bottom: 1px solid var(--border-soft); }
    .step-card:last-child { border-bottom: none; }
    .proc-header     { flex-direction: column; align-items: flex-start; margin-bottom: 48px; }
    .proc-bottom     { flex-direction: column; align-items: flex-start; }
  }
`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const STEPS = [
  {
    num: '01',
    phase: 'Phase One',
    title: 'Discover',
    desc: 'Deep dive into your brand, goals, audience, and market position to build a solid strategic foundation.',
    deliverables: ['Brand & competitor audit', 'Audience persona mapping', 'Goals & KPI alignment'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
      </svg>
    ),
  },
  {
    num: '02',
    phase: 'Phase Two',
    title: 'Strategize',
    desc: 'Craft a comprehensive roadmap that aligns creative vision with measurable business objectives.',
    deliverables: ['Channel & content strategy', 'Project roadmap & timeline', 'Budget & resource plan'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    ),
  },
  {
    num: '03',
    phase: 'Phase Three',
    title: 'Execute',
    desc: 'Build, design, and launch with meticulous attention to detail and unwavering quality standards.',
    deliverables: ['Design & development', 'QA testing & review cycles', 'Staged launch & deployment'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
  },
  {
    num: '04',
    phase: 'Phase Four',
    title: 'Optimize',
    desc: 'Continuously analyze, iterate, and scale what works — because growth never stops.',
    deliverables: ['Performance reports', 'A/B test results', 'Scaling recommendations'],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
];

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const LandingProcess = () => (
  <>
    <style>{styles}</style>

    <section className="process-section" id="process">

      {/* Header */}
      <div className="proc-header">
        <div>
          <p className="proc-eyebrow">How We Work</p>
          <h2 className="proc-heading">
            Our <em>Process</em>
          </h2>
        </div>
        <p className="proc-sub">
          A proven four-step framework that turns ambition into measurable
          achievement — every time.
        </p>
      </div>

      {/* Steps */}
      <div className="steps-grid">
        {STEPS.map((s) => (
          <div key={s.num} className="step-card">
            {/* Ghost bg number */}
            <div className="step-ghost">{s.num}</div>

            {/* Phase label */}
            <p className="step-phase">
              <span className="step-phase-dot" />
              {s.phase}
            </p>

            {/* Number badge */}
            <div className="step-badge">{s.num}</div>

            {/* Icon */}
            <div className="step-icon">{s.icon}</div>

            {/* Title + description */}
            <p className="step-title">{s.title}</p>
            <p className="step-desc">{s.desc}</p>

            {/* Deliverables */}
            <div className="step-deliverables">
              <p className="step-del-label">Deliverables</p>
              {s.deliverables.map((d) => (
                <div key={d} className="step-del-item">{d}</div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Bottom row */}
      <div className="proc-bottom">
        <p className="proc-note">
          Average project timeline: <strong>4–12 weeks</strong> depending on
          scope. Every phase includes weekly check-ins and transparent reporting.
        </p>
        <a href="#contact" className="proc-cta">
          Start Your Project
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </a>
      </div>

    </section>
  </>
);

export default LandingProcess;